import React, { useState, useEffect, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { portfolioReportsAPI } from '../../services/api';
import LayoutEditor from './LayoutEditor';
import AddBlockModal from './AddBlockModal';
import PropagateModal from './PropagateModal';
import { BLOCK_REGISTRY, DEFAULT_LAYOUT, getBlockDef } from './BlockRenderer';
import useReportEditor from './useReportEditor';
import ColourPaletteEditor from './ColourPaletteEditor';

const EditableContentPanel = forwardRef(({ content, portfolioCode, portfolioName, quarter, branding, onSave, onChange, portfolios, domain, overflowBlocks, onLayoutModeChange }, ref) => {
  // ── Centralised state via useReportEditor hook (useReducer + undo/redo) ──
  const {
    formData, canUndo, canRedo,
    setField, setLayout, addBlock, removeBlock,
    addPage, removePage, setCustomContent, setHeading, setHeadingStyle, setBlockDef, setPageBlocks,
    resetAll, undo, redo,
  } = useReportEditor(content, onChange);

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('editor');
  const [addBlockTarget, setAddBlockTarget] = useState(null); // page index or null
  const [showPropagate, setShowPropagate] = useState(false);

  // Expose editor actions to parent for inline layout + text editing on the preview
  useImperativeHandle(ref, () => ({
    handleBlockChange: (pageIndex, blockId, updatedBlock) => {
      const layout = formData.page_layout || DEFAULT_LAYOUT;
      const newBlocks = layout[pageIndex]?.blocks.map(b =>
        (typeof b === 'string' ? b : b.id) === blockId ? updatedBlock : b
      );
      if (newBlocks) handlePageBlocksChange(pageIndex, newBlocks);
    },
    // Batch variant — apply many block updates in ONE dispatch. Looping over
    // handleBlockChange rebuilds from the same stale formData snapshot each
    // call, so only the last block's change would survive (Autofit All).
    handleBlocksChange: (pageIndex, updatesById) => {
      const layout = formData.page_layout || DEFAULT_LAYOUT;
      const newBlocks = layout[pageIndex]?.blocks.map(b => {
        const id = typeof b === 'string' ? b : b.id;
        return updatesById[id] || b;
      });
      if (newBlocks) handlePageBlocksChange(pageIndex, newBlocks);
    },
    handleRemoveBlock: removeBlock,
    handleAddBlock: (pageIndex) => setAddBlockTarget(pageIndex),
    setField,
    setCustomContent,
    setHeading,
    setHeadingStyle,
    setBlockDef,
    formData,
  }), [formData, removeBlock, setPageBlocks, setAddBlockTarget, setField, setCustomContent, setHeading, setHeadingStyle, setBlockDef]);

  // Layout mode always active when editor panel is visible
  useEffect(() => {
    if (onLayoutModeChange) {
      onLayoutModeChange(true);
    }
    return () => {
      if (onLayoutModeChange) onLayoutModeChange(false);
    };
  }, [onLayoutModeChange]);

  // Version history state
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(null);

  // Keyboard shortcut for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // Wrapped handlers that delegate to the hook
  const handleChange = useCallback((key, value) => setField(key, value), [setField]);
  const handleLayoutChange = useCallback((newLayout) => setLayout(newLayout), [setLayout]);
  const handleAddBlock = useCallback((pageIndex, blockId, blockDef, imageData) => addBlock(pageIndex, blockId, blockDef, imageData), [addBlock]);
  const handleRemoveBlock = useCallback((blockId) => removeBlock(blockId), [removeBlock]);
  const handleAddPage = useCallback(() => addPage(), [addPage]);
  const handleRemovePage = useCallback((pageIndex) => removePage(pageIndex), [removePage]);
  const handleCustomContentChange = useCallback((blockId, html) => setCustomContent(blockId, html), [setCustomContent]);
  const handlePageBlocksChange = useCallback((pageIdx, newBlocks) => setPageBlocks(pageIdx, newBlocks), [setPageBlocks]);

  const handleDeleteAllContent = useCallback(async () => {
    if (!window.confirm('Delete all content and reset this report to a blank layout? This will save immediately.')) return;
    const blankData = resetAll();
    try {
      await onSave(blankData);
      setMessage({ type: 'success', text: 'All content deleted — report reset to blank' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const text = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(e => e.msg).join('; ')
        : 'Error resetting content';
      setMessage({ type: 'error', text });
    }
  }, [onSave, resetAll]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await onSave(formData);
      setMessage({ type: 'success', text: 'Content saved successfully' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const text = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(e => e.msg).join('; ')
        : 'Error saving content';
      setMessage({ type: 'error', text });
    } finally {
      setSaving(false);
    }
  };

  // ── Version History Handlers ──
  const loadVersions = useCallback(async () => {
    if (!portfolioCode || !quarter) return;
    setVersionsLoading(true);
    try {
      const result = await portfolioReportsAPI.listVersions(portfolioCode, quarter, domain || '_default');
      setVersions(result.versions || []);
    } catch (err) {
      console.error('Error loading version history:', err);
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  }, [portfolioCode, quarter, domain]);

  // Load versions when switching to the history tab
  useEffect(() => {
    if (activeTab === 'history') {
      loadVersions();
    }
  }, [activeTab, loadVersions]);

  const handleRestoreVersion = useCallback(async (versionId) => {
    if (!window.confirm('Restore this version? The current content will be saved as a new version before restoring.')) return;
    setRestoringVersion(versionId);
    try {
      await portfolioReportsAPI.restoreVersion(versionId);
      setMessage({ type: 'success', text: 'Version restored successfully. Reloading...' });
      // Reload the report content to reflect restored version
      if (onSave) {
        // Trigger a reload by re-fetching — the parent handles this via loadContentOnly
        window.location.reload();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Error restoring version' });
    } finally {
      setRestoringVersion(null);
    }
  }, [onSave]);

  return (
    <div className="editable-content-panel">
      {showPropagate && (
        <PropagateModal
          portfolios={portfolios}
          currentPortfolioCode={portfolioCode}
          quarter={quarter}
          domain={domain}
          formData={formData}
          onClose={() => setShowPropagate(false)}
          onComplete={() => setShowPropagate(false)}
        />
      )}
      <div className="panel-sticky-header">
        <div className="panel-header">
          <h3>Edit Quarterly Report</h3>
          <div className="panel-header-actions">
            <button
              className="btn-undo"
              onClick={undo}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
            >
              Undo
            </button>
            <button
              className="btn-redo"
              onClick={redo}
              disabled={!canRedo}
              title="Redo (Ctrl+Y)"
            >
              Redo
            </button>
            <button
              className="btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            {portfolios && portfolios.length > 1 && (
              <button
                className="btn-propagate-open"
                onClick={() => setShowPropagate(true)}
              >
                Propagate
              </button>
            )}
            <button
              className="btn-delete-all"
              onClick={handleDeleteAllContent}
            >
              Delete All
            </button>
          </div>
        </div>

        {/* Tab toggle */}
        <div className="editor-tab-bar">
          <button
            className={`editor-tab${activeTab === 'editor' ? ' active' : ''}`}
            onClick={() => setActiveTab('editor')}
          >
            Editor
          </button>
          <button
            className={`editor-tab${activeTab === 'history' ? ' active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
          <button
            className={`editor-tab${activeTab === 'colours' ? ' active' : ''}`}
            onClick={() => setActiveTab('colours')}
          >
            Colours
          </button>
        </div>
        {message && (
          <div className={`panel-message ${message.type}`}>{message.text}</div>
        )}
      </div>

      {/* ── Editor Tab (Layout + Title + Disclaimer) ── */}
      {activeTab === 'editor' && (
        <>
          <LayoutEditor
            layout={formData.page_layout || DEFAULT_LAYOUT}
            onLayoutChange={handleLayoutChange}
            onAddBlock={(pageIndex) => setAddBlockTarget(pageIndex)}
            onRemoveBlock={handleRemoveBlock}
            onAddPage={handleAddPage}
            onRemovePage={handleRemovePage}
            customBlocks={formData.custom_blocks}
          />

          <AddBlockModal
            isOpen={addBlockTarget !== null}
            onClose={() => setAddBlockTarget(null)}
            onAdd={(blockId, blockDef, imageData) => handleAddBlock(addBlockTarget, blockId, blockDef, imageData)}
            currentLayout={formData.page_layout || DEFAULT_LAYOUT}
          />

          {/* Title Box */}
          <div className="content-page-divider">Title</div>
          {(() => {
            const titleData = formData.title_box || {};
            const defaultAccent = branding?.secondary_color || '#599BE3';
            return (
              <div className="section-editor">
                <div className="title-box-fields">
                  {!branding && (
                    <>
                      <div className="field-row">
                        <label className="field-label">Title Text</label>
                        <input
                          type="text"
                          className="field-input"
                          value={titleData.title_text || ''}
                          onChange={(e) => handleChange('title_box', { ...titleData, title_text: e.target.value })}
                          placeholder="Drummond Strategic Series (leave blank for default)"
                        />
                      </div>
                      <div className="field-row">
                        <label className="field-label">Title Code</label>
                        <input
                          type="text"
                          className="field-input"
                          value={titleData.title_code || ''}
                          onChange={(e) => handleChange('title_box', { ...titleData, title_code: e.target.value })}
                          placeholder="e.g. DS70"
                        />
                      </div>
                      <div className="field-row">
                        <label className="field-label">Accent</label>
                        <div className="colour-picker-row">
                          <input
                            type="color"
                            className="field-colour"
                            value={titleData.accent_colour || defaultAccent}
                            onChange={(e) => handleChange('title_box', { ...titleData, accent_colour: e.target.value })}
                          />
                          <input
                            type="text"
                            className="field-input field-input-short"
                            value={titleData.accent_colour || defaultAccent}
                            onChange={(e) => handleChange('title_box', { ...titleData, accent_colour: e.target.value })}
                            placeholder={defaultAccent}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="field-row">
                    <label className="field-label">Subtitle</label>
                    <input
                      type="text"
                      className="field-input"
                      value={titleData.subtitle || ''}
                      onChange={(e) => handleChange('title_box', { ...titleData, subtitle: e.target.value })}
                      placeholder="Quarterly Review – December 2025 (leave blank for default)"
                    />
                  </div>
                </div>
              </div>
            );
          })()}

        </>
      )}

      {/* ── Colours Tab ── */}
      {activeTab === 'colours' && (
        <ColourPaletteEditor
          formData={formData}
          setField={handleChange}
          branding={branding}
        />
      )}

      {/* ── History Tab ── */}
      {activeTab === 'history' && (
        <div className="version-history-panel">
          <p className="version-history-info">
            Previous versions are automatically saved each time content is updated.
            Restoring a version will save the current content as a new version first.
          </p>
          {versionsLoading && (
            <div className="text-center" style={{ padding: '2rem' }}>
              <div className="loading-spinner" />
              <p style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '0.5rem' }}>Loading version history...</p>
            </div>
          )}
          {!versionsLoading && versions.length === 0 && (
            <p className="version-history-empty">No previous versions found. Versions are created each time you save.</p>
          )}
          {!versionsLoading && versions.length > 0 && (
            <div className="version-list">
              {versions.map((v, i) => (
                <div key={v.version_id} className="version-item">
                  <div className="version-meta">
                    <span className="version-date">
                      {v.saved_at ? new Date(v.saved_at).toLocaleString('en-AU', {
                        day: 'numeric', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      }) : 'Unknown date'}
                    </span>
                    <span className="version-user">{v.saved_by || 'Unknown'}</span>
                    {i === 0 && <span className="version-latest-badge">Most Recent</span>}
                  </div>
                  <button
                    className="btn-restore-version"
                    onClick={() => handleRestoreVersion(v.version_id)}
                    disabled={restoringVersion !== null}
                  >
                    {restoringVersion === v.version_id ? 'Restoring...' : 'Restore'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default EditableContentPanel;
